import { useMemo, useState, useRef, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";

// See PdfPageViewer.jsx for why this loads from a plain `public/`-folder
// `.js` file instead of webpack's hashed `.mjs` asset pipeline.
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

// See PdfPageViewer.jsx — disables pdf.js HTTP Range probing/requests, which
// live reverse proxies/CDNs in front of this token-gated endpoint have been
// observed to corrupt. Module-level so the object identity is stable.
const PDF_LOAD_OPTIONS = { disableRange: true, disableStream: true };

/**
 * Renders EVERY page of a PDF in a scrollable column — a signer must be
 * able to review the whole document, not just the page holding their
 * signature box. One <Document> load, many <Page>s (single fetch).
 * `pageRefs` (a ref to a plain object, keyed by 1-based page number) lets
 * the caller scroll to a specific page on demand — e.g. a persistent
 * "Jump to signature" button that stays on screen while scrolling.
 *
 * `width` is a MAX width (the desktop cap) — the actual rendered width is
 * clamped to the wrapping container's real measured size via
 * ResizeObserver, so the document shrinks to fit narrow phone screens in
 * either orientation (instead of forcing horizontal scroll on a signer
 * opening the emailed link on mobile) and grows back up to `width` on
 * wider viewports / after an orientation change. `<Page>` renders onto a
 * fixed-pixel canvas internally, so this JS measurement is required —
 * CSS `max-width` alone can't shrink a canvas that already has an
 * explicit pixel width.
 */
export function PdfMultiPageViewer({ file, pageCount, width = 640, pageRefs, renderOverlay }) {
  const fileIdentity = typeof file === "string" ? file : file?.url || "";
  const stableFile = useMemo(() => file, [fileIdentity]); // eslint-disable-line react-hooks/exhaustive-deps
  const containerRef = useRef(null);
  const [renderWidth, setRenderWidth] = useState(width);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => setRenderWidth(Math.max(220, Math.min(width, Math.floor(el.clientWidth))));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [width]);

  return (
    <div ref={containerRef} className="w-full" data-testid="pdf-multi-page-viewer">
      <Document key={fileIdentity} file={stableFile} options={PDF_LOAD_OPTIONS} loading={<div className="p-16 text-sm text-neutral-400">Loading PDF…</div>} error={<div className="p-16 text-sm text-red-500">Couldn't load this PDF.</div>}>
        {Array.from({ length: pageCount }, (_, i) => i + 1).map((num) => (
          <div key={num} ref={(el) => { if (pageRefs) pageRefs.current[num] = el; }} className="relative border border-neutral-300 bg-white select-none mb-4 mx-auto" style={{ width: "fit-content" }}>
            <Page pageNumber={num} width={renderWidth} renderTextLayer={false} renderAnnotationLayer={false} />
            {renderOverlay && renderOverlay(num)}
          </div>
        ))}
      </Document>
    </div>
  );
}
